import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import { composeCaption, type GeneratedContentSummary } from '../api/content'
import {
  mediaApi,
  formatDuration,
  aspectRatio,
  type MediaAsset,
} from '../api/media'
import {
  publishApi,
  privacyLabel,
  publishErrorText,
  publishStatusText,
  type CreatorInfo,
  type PublishResult,
  type PublishStatus,
} from '../api/publish'
import { socialAccountsApi, type ConnectedAccount } from '../api/socialAccounts'

type Phase = 'form' | 'publishing' | 'tracking'

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; detail?: string } | null
    return publishErrorText(body?.error, body?.detail) ?? body?.error ?? `${fallback} (HTTP ${err.status})`
  }
  return fallback
}

/** Blockerande problem från servervakten (422 publish_gate_failed). */
function gateProblems(err: unknown): string[] {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; problems?: string[] } | null
    if (body?.error === 'publish_gate_failed' || body?.error === 'draft_text_not_publishable')
      return body.problems ?? []
  }
  return []
}

const TERMINAL = new Set(['Published', 'Failed'])

export function TikTokPublishDialog({
  draft,
  onClose,
  onPublished,
}: {
  draft: GeneratedContentSummary
  onClose: () => void
  onPublished: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  // Öppna som äkta modal → fokusfälla + Escape + inert bakgrund från plattformen.
  useEffect(() => {
    const dlg = dialogRef.current
    dlg?.showModal()
    return () => dlg?.close()
  }, [])

  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [account, setAccount] = useState<ConnectedAccount | null>(null)
  const [media, setMedia] = useState<MediaAsset[]>([])
  const [creator, setCreator] = useState<CreatorInfo | null>(null)

  // Formulärval
  const [mediaAssetId, setMediaAssetId] = useState('')
  const [privacyLevel, setPrivacyLevel] = useState('') // INGET förval — TikTok-krav
  const [allowComment, setAllowComment] = useState(true)
  const [allowDuet, setAllowDuet] = useState(true)
  const [allowStitch, setAllowStitch] = useState(true)
  const [brandContent, setBrandContent] = useState(false)
  const [ownerDeclaredAi, setOwnerDeclaredAi] = useState(false)
  const [musicConfirmed, setMusicConfirmed] = useState(false)
  const [consent, setConsent] = useState(false)

  const [phase, setPhase] = useState<Phase>('form')
  const [result, setResult] = useState<PublishResult | null>(null)
  const [status, setStatus] = useState<PublishStatus | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [problems, setProblems] = useState<string[]>([])
  const [live, setLive] = useState('')
  // Driver polling-loopen. Bumpas i BÅDA utfallen (success och fel) så en enda
  // transient miss inte dödar auto-uppdateringen för resten av dialogens livstid.
  const [pollTick, setPollTick] = useState(0)

  // ---- inläsning: konton (TikTok, anslutna) + video som passar TikTok ----
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const [accs, assets] = await Promise.all([
          socialAccountsApi.list(),
          mediaApi.list(),
        ])
        if (cancelled) return
        const tiktok = accs.find((a) => a.platform === 'TikTok' && a.status === 'Connected') ?? null
        setAccount(tiktok)
        setMedia(assets.filter(tiktokPublishable))
        setLoadState('ready')
        if (tiktok) void fetchCreator(tiktok.id)
      } catch {
        if (!cancelled) {
          setLoadState('error')
          setLoadError('Kunde inte läsa in konto och videor.')
        }
      }
    }
    void load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchCreator = useCallback(async (accountId: string) => {
    try {
      setCreator(await publishApi.tiktokCreatorInfo(accountId))
    } catch (err) {
      setFormError(errorText(err, 'Kunde inte hämta kontoinfo från TikTok.'))
    }
  }, [])

  const selectedMedia = media.find((m) => m.id === mediaAssetId) ?? null
  const isAiMedia = selectedMedia?.source === 'VideoAi'
  const effectiveAigc = isAiMedia || ownerDeclaredAi

  const caption = composeCaption(draft.body, draft.callToAction, draft.hashtags)

  // Kontot kan ha stängt av interaktioner. Då MÅSTE de vara av (servervakten kräver
  // det) — kryssrutan tvingas av och låses.
  const commentLocked = creator?.commentDisabled ?? false
  const duetLocked = creator?.duetDisabled ?? false
  const stitchLocked = creator?.stitchDisabled ?? false

  const canSubmit =
    phase === 'form' &&
    !!account &&
    !!creator &&
    mediaAssetId !== '' &&
    privacyLevel !== '' &&
    musicConfirmed &&
    consent

  async function onPublish() {
    if (!account || !canSubmit) return
    setPhase('publishing')
    setFormError(null)
    setProblems([])
    setLive('Publicerar till TikTok …')
    try {
      const res = await publishApi.publishTikTok({
        accountId: account.id,
        generatedContentId: draft.id,
        mediaAssetId,
        privacyLevel,
        disableComment: commentLocked || !allowComment,
        disableDuet: duetLocked || !allowDuet,
        disableStitch: stitchLocked || !allowStitch,
        brandOrganicToggle: false,
        brandContentToggle: brandContent,
        ownerDeclaredAi,
        musicUsageConfirmed: musicConfirmed,
        consent,
      })
      setResult(res)
      setPhase('tracking')
      setLive('Skickat till TikTok. Bearbetas.')
      onPublished()
    } catch (err) {
      setPhase('form')
      setProblems(gateProblems(err))
      setFormError(errorText(err, 'Publiceringen misslyckades.'))
      setLive('Publiceringen misslyckades.')
    }
  }

  // Manuell statushämtning (knapp i spårningsvyn). Väcker även loopen om den
  // mot förmodan hann stanna.
  const refreshStatus = useCallback(async () => {
    if (!result) return
    try {
      const s = await publishApi.status(result.scheduledPostId)
      setStatus(s)
      if (TERMINAL.has(s.status)) setLive(publishStatusText(s.status, s.rawStatus).text)
      else setPollTick((n) => n + 1)
    } catch {
      setLive('Kunde inte hämta status just nu.')
    }
  }, [result])

  // ---- statuspolling när posten skickats ----
  // Loopen re-armeras via pollTick, som bumpas i BÅDE success- och fel-grenen.
  // (Tidigare drevs re-arm av att `status` ändrades — då dog pollingen permanent
  // vid ett enda transient fel, eftersom catch inte rörde någon state. Bekräftat av
  // granskningsworkflow 2026-07-11.) Stoppar vid terminalt tillstånd.
  useEffect(() => {
    if (phase !== 'tracking' || !result) return
    if (status && TERMINAL.has(status.status)) return
    const t = setTimeout(async () => {
      try {
        const s = await publishApi.status(result.scheduledPostId)
        setStatus(s)
        if (TERMINAL.has(s.status)) {
          setLive(publishStatusText(s.status, s.rawStatus).text)
          return // terminalt → re-arma inte
        }
      } catch {
        /* transient — re-arma nedan i stället för att dö */
      }
      setPollTick((n) => n + 1)
    }, 4000)
    return () => clearTimeout(t)
  }, [phase, result, status, pollTick])

  const titleId = 'pub-title'

  return (
    <dialog
      ref={dialogRef}
      className="publish-dialog"
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={onClose}
    >
      <form method="dialog" className="publish-form" onSubmit={(e) => e.preventDefault()}>
        <div className="platform-head">
          <h2 id={titleId}>Publicera till TikTok</h2>
          <button type="button" className="link" onClick={onClose} aria-label="Stäng">
            Stäng
          </button>
        </div>

        {/* Skärmläsarens kanal för pågående/klart. Tom vid mount. */}
        <p role="status" className="sr-only">
          {live}
        </p>

        {loadState === 'loading' && <p className="muted">Läser in …</p>}
        {loadState === 'error' && <p className="field-error">{loadError}</p>}

        {loadState === 'ready' && !account && (
          <p className="banner banner-error">
            Inget anslutet TikTok-konto. Anslut kontot på Konton-sidan innan du publicerar.
          </p>
        )}

        {loadState === 'ready' && account && phase !== 'tracking' && (
          <>
            <p className="muted small">
              Konto: <strong>{creator?.nickname ?? account.accountName}</strong>
              {creator && ` (@${creator.username})`}
            </p>

            {/* Caption-förhandsvisning (skrivskyddad — redigeras på utkastet) */}
            <div className="field">
              <label htmlFor="pub-caption">Bildtext som publiceras</label>
              <textarea id="pub-caption" rows={4} value={caption} readOnly aria-describedby="pub-caption-count" />
              <p id="pub-caption-count" className={caption.length > 2200 ? 'char-count over' : 'char-count'}>
                {caption.length} / 2200 tecken
              </p>
            </div>

            {/* Videoväljare — bara filer som passar TikTok */}
            <div className="field">
              <label htmlFor="pub-media">Video</label>
              {media.length === 0 ? (
                <p className="field-error">
                  Ingen uppladdad video passar TikTok (9:16, 23–60 fps, H.264/H.265).
                  Ladda upp en på Media-sidan.
                </p>
              ) : (
                <select
                  id="pub-media"
                  value={mediaAssetId}
                  onChange={(e) => setMediaAssetId(e.target.value)}
                  required
                >
                  <option value="" disabled>
                    Välj video …
                  </option>
                  {media.map((m) => (
                    <option key={m.id} value={m.id}>
                      {describeMedia(m)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Synlighet — INGET förval (TikTok-krav, mest granskade grinden) */}
            <div className="field">
              <label htmlFor="pub-privacy">Synlighet</label>
              <select
                id="pub-privacy"
                value={privacyLevel}
                onChange={(e) => setPrivacyLevel(e.target.value)}
                required
                aria-describedby="pub-privacy-hint"
                disabled={!creator}
              >
                <option value="" disabled>
                  Välj synlighet …
                </option>
                {creator?.privacyLevelOptions.map((level) => (
                  <option key={level} value={level}>
                    {privacyLabel(level)}
                  </option>
                ))}
              </select>
              <p id="pub-privacy-hint" className="muted small">
                {creator && creator.privacyLevelOptions.length === 1
                  ? 'Appen är inte granskad ännu, så bara privat publicering är möjlig.'
                  : 'TikTok tillåter inget förval — välj själv.'}
              </p>
            </div>

            {/* Interaktioner. Har kontot stängt av en, låses den av. */}
            <fieldset className="field surfaces">
              <legend>Interaktioner</legend>
              <Toggle
                id="pub-comment"
                label="Tillåt kommentarer"
                checked={!commentLocked && allowComment}
                disabled={commentLocked}
                lockedNote={commentLocked ? 'Avstängt av ditt konto' : undefined}
                onChange={setAllowComment}
              />
              <Toggle
                id="pub-duet"
                label="Tillåt duett"
                checked={!duetLocked && allowDuet}
                disabled={duetLocked}
                lockedNote={duetLocked ? 'Avstängt av ditt konto' : undefined}
                onChange={setAllowDuet}
              />
              <Toggle
                id="pub-stitch"
                label="Tillåt stitch"
                checked={!stitchLocked && allowStitch}
                disabled={stitchLocked}
                lockedNote={stitchLocked ? 'Avstängt av ditt konto' : undefined}
                onChange={setAllowStitch}
              />
            </fieldset>

            {/* Branded content + AI-flagga */}
            <fieldset className="field surfaces">
              <legend>Innehållsmärkning</legend>
              <Toggle
                id="pub-brand"
                label="Betalt partnerskap (branded content)"
                checked={brandContent}
                onChange={setBrandContent}
                hint={brandContent ? 'Kan inte publiceras privat.' : undefined}
              />
              <Toggle
                id="pub-aigc"
                label="AI-genererat innehåll"
                checked={effectiveAigc}
                disabled={isAiMedia}
                lockedNote={isAiMedia ? 'AI-video flaggas automatiskt (AI-förordningen art. 50.4)' : undefined}
                onChange={setOwnerDeclaredAi}
              />
            </fieldset>

            {/* Obligatoriska bekräftelser */}
            <fieldset className="field surfaces">
              <legend>Bekräfta innan publicering</legend>
              <Toggle
                id="pub-music"
                label="Jag bekräftar att musikanvändningen följer TikToks musikvillkor"
                checked={musicConfirmed}
                onChange={setMusicConfirmed}
              />
              <Toggle
                id="pub-consent"
                label="Jag godkänner att videon överförs till TikTok för publicering"
                checked={consent}
                onChange={setConsent}
              />
            </fieldset>

            {problems.length > 0 && (
              <div role="alert" className="banner banner-error">
                <p>TikTok eller våra regler stoppade publiceringen:</p>
                <ul className="issue-list">
                  {problems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            )}
            {formError && problems.length === 0 && (
              <div role="alert" className="banner banner-error">
                {formError}
              </div>
            )}

            <div className="draft-actions">
              <button
                type="button"
                className="primary"
                disabled={!canSubmit}
                onClick={() => void onPublish()}
              >
                {phase === 'publishing' ? 'Publicerar …' : 'Publicera till TikTok'}
              </button>
              <button type="button" className="link" onClick={onClose}>
                Avbryt
              </button>
            </div>
          </>
        )}

        {phase === 'tracking' && result && (
          <TrackingView status={status} onDone={onClose} onRefresh={() => void refreshStatus()} />
        )}
      </form>
    </dialog>
  )
}

function TrackingView({
  status,
  onDone,
  onRefresh,
}: {
  status: PublishStatus | null
  onDone: () => void
  onRefresh: () => void
}) {
  const view = status
    ? publishStatusText(status.status, status.rawStatus)
    : { text: 'Skickat till TikTok. Bearbetas …', kind: 'warn' as const }
  const done = status ? TERMINAL.has(status.status) : false

  // Publiceringsknappen försvann när fasen bytte; flytta fokus hit så
  // tangentbordsanvändaren inte tappar sin plats (WCAG 2.4.3).
  const headingRef = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  return (
    <div className="publish-tracking">
      <p ref={headingRef} tabIndex={-1} className={`badge badge-${view.kind}`}>
        {view.text}
      </p>
      {status?.failReason && <p className="field-error">Orsak: {status.failReason}</p>}
      {!done && (
        <p className="muted small">
          Statusen uppdateras automatiskt. Publiceringen kan ta någon minut.
        </p>
      )}
      <div className="draft-actions">
        {!done && (
          <button type="button" className="link" onClick={onRefresh}>
            Uppdatera status
          </button>
        )}
        <button type="button" className={done ? 'primary' : 'link'} onClick={onDone}>
          {done ? 'Klar' : 'Stäng och fortsätt i bakgrunden'}
        </button>
      </div>
    </div>
  )
}

/** En kryssruta med etikett, valfri låsnotis och hint. 44 px träffyta via .checkbox-row. */
function Toggle({
  id,
  label,
  checked,
  disabled,
  lockedNote,
  hint,
  onChange,
}: {
  id: string
  label: string
  checked: boolean
  disabled?: boolean
  lockedNote?: string
  hint?: string
  onChange: (v: boolean) => void
}) {
  const noteId = lockedNote || hint ? `${id}-note` : undefined
  return (
    <div className="checkbox-block">
      <label className="checkbox-row" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-describedby={noteId}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{label}</span>
      </label>
      {(lockedNote || hint) && (
        <p id={noteId} className="muted small checkbox-note">
          {lockedNote ?? hint}
        </p>
      )}
    </div>
  )
}

function describeMedia(m: MediaAsset): string {
  const parts = [
    m.width && m.height ? `${m.width}×${m.height}` : null,
    aspectRatio(m.width, m.height),
    formatDuration(m.durationSeconds),
    m.source === 'VideoAi' ? 'AI' : null,
  ].filter(Boolean)
  return parts.join(' · ') || m.id.slice(0, 8)
}

/** Passar filen TikToks krav? Speglar EvaluateTikTok via serverns SurfaceCompatibility. */
function tiktokPublishable(m: MediaAsset): boolean {
  return m.surfaces.find((s) => s.surface === 'TikTok')?.canPublish ?? false
}
